import { useSyncExternalStore } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { youtubeApi } from '@/api/endpoints';
import { queryKeys } from './queryKeys';
import type {
  UpdateYouTubeVideoInput,
  YouTubeUploadDefaults,
  YouTubeVideoSettings,
} from '@/types/youtube';
import {
  type DirectUploadEntry,
  directUploadsSnapshot,
  subscribeDirectUploads,
} from '@/utils/youtubeUploads';

/**
 * The YouTube section's data.
 *
 * Every read here costs quota from a pool the whole deployment shares, so the
 * stale times are longer than the app's 30-second default and nothing refetches
 * on focus: a channel's video list does not need to be re-read because someone
 * switched tabs.
 */
const CHANNEL_STALE_MS = 60_000;
const REFERENCE_STALE_MS = 24 * 60 * 60 * 1000;

export function useYouTubeConfig() {
  return useQuery({
    queryKey: queryKeys.youtube.config,
    queryFn: () => youtubeApi.config(),
    staleTime: CHANNEL_STALE_MS,
  });
}

export function useYouTubeOverview(accountId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.youtube.overview(accountId ?? ''),
    queryFn: () => youtubeApi.overview(accountId!),
    enabled: Boolean(accountId),
    staleTime: CHANNEL_STALE_MS,
  });
}

/** The channel's uploads, a page at a time — "Load more" at the bottom. */
export function useYouTubeVideos(
  accountId: string | undefined,
  type: 'all' | 'videos' | 'shorts'
) {
  return useInfiniteQuery({
    queryKey: queryKeys.youtube.videos(accountId ?? '', { type }),
    queryFn: ({ pageParam }) =>
      youtubeApi.videos(accountId!, {
        type,
        pageToken: (pageParam as string | undefined) || undefined,
      }),
    initialPageParam: '' as string,
    getNextPageParam: (last) => last.nextPageToken ?? undefined,
    enabled: Boolean(accountId),
    staleTime: CHANNEL_STALE_MS,
  });
}

/**
 * Our own drafts and in-flight uploads for a channel. Polled while anything
 * is uploading, so progress bars move.
 */
export function useYouTubeQueue(accountId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.youtube.queue(accountId ?? ''),
    queryFn: () => youtubeApi.queue(accountId!),
    enabled: Boolean(accountId),
    refetchInterval: (query) =>
      query.state.data?.scheduled.some(
        (item) =>
          item.status === 'POSTING' &&
          item.upload &&
          item.upload.status !== 'UPLOADED'
      )
        ? 5_000
        : false,
  });
}

export function useYouTubeVideo(
  accountId: string | undefined,
  videoId: string | undefined,
  includePlaylists = true
) {
  return useQuery({
    queryKey: queryKeys.youtube.video(accountId ?? '', videoId ?? ''),
    queryFn: () => youtubeApi.video(accountId!, videoId!, includePlaylists),
    enabled: Boolean(accountId && videoId),
    staleTime: CHANNEL_STALE_MS,
  });
}

export function useYouTubePlaylists(accountId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.youtube.playlists(accountId ?? ''),
    queryFn: () => youtubeApi.playlists(accountId!),
    enabled: Boolean(accountId),
    staleTime: CHANNEL_STALE_MS,
  });
}

export function useYouTubeCategories(accountId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.youtube.categories(accountId ?? ''),
    queryFn: () => youtubeApi.categories(accountId!),
    enabled: Boolean(accountId),
    staleTime: REFERENCE_STALE_MS,
  });
}

export function useYouTubeLanguages(accountId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.youtube.languages(accountId ?? ''),
    queryFn: () => youtubeApi.languages(accountId!),
    enabled: Boolean(accountId),
    staleTime: REFERENCE_STALE_MS,
  });
}

export function useYouTubeUploadDefaults(accountId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.youtube.defaults(accountId ?? ''),
    queryFn: () => youtubeApi.uploadDefaults(accountId!),
    enabled: Boolean(accountId),
  });
}

export function useYouTubeBestTimes(accountId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.youtube.bestTimes(accountId ?? ''),
    queryFn: () => youtubeApi.bestTimes(accountId!),
    enabled: Boolean(accountId),
    staleTime: 60 * 60 * 1000,
  });
}

/** Upload progress for targets, polled while any is still moving. */
export function useYouTubeUploads(targetIds: string[]) {
  return useQuery({
    queryKey: queryKeys.youtube.uploads(targetIds),
    queryFn: () => youtubeApi.uploads(targetIds),
    enabled: targetIds.length > 0,
    refetchInterval: (query) =>
      query.state.data?.some(
        (upload) => upload.status === 'PENDING' || upload.status === 'UPLOADING'
      )
        ? 5_000
        : false,
  });
}

function useInvalidateYouTube() {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.youtube.all });
}

export function useUpdateYouTubeVideo(accountId: string) {
  const invalidate = useInvalidateYouTube();
  return useMutation({
    mutationFn: (input: { videoId: string } & UpdateYouTubeVideoInput) => {
      const { videoId, ...rest } = input;
      return youtubeApi.updateVideo(accountId, videoId, rest);
    },
    onSuccess: invalidate,
  });
}

export function useDeleteYouTubeVideo(accountId: string) {
  const invalidate = useInvalidateYouTube();
  return useMutation({
    mutationFn: (videoId: string) => youtubeApi.deleteVideo(accountId, videoId),
    onSuccess: invalidate,
  });
}

export function useCreateYouTubePlaylist(accountId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      title: string;
      description?: string;
      privacyStatus?: string;
    }) => youtubeApi.createPlaylist(accountId, input),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: queryKeys.youtube.playlists(accountId),
      }),
  });
}

export function useUpdateYouTubePlaylist(accountId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      playlistId: string;
      title: string;
      description?: string;
      privacyStatus?: string;
    }) => {
      const { playlistId, ...rest } = input;
      return youtubeApi.updatePlaylist(accountId, playlistId, rest);
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: queryKeys.youtube.playlists(accountId),
      }),
  });
}

export function useSaveYouTubeUploadDefaults(accountId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: YouTubeUploadDefaults) =>
      youtubeApi.saveUploadDefaults(accountId, input),
    onSuccess: (saved) =>
      queryClient.setQueryData(queryKeys.youtube.defaults(accountId), saved),
  });
}

export function useUploadCaptionFile() {
  return useMutation({
    mutationFn: (file: File) => youtubeApi.uploadCaptionFile(file),
  });
}

export function useGenerateYouTubeMetadata() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: youtubeApi.generateMetadata,
    // It spent an AI call.
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.aiUsage.all }),
  });
}

export function useYouTubeBulkDrafts() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      accountId: string;
      items: {
        mediaId: string;
        title?: string;
        description?: string;
        settings?: Partial<YouTubeVideoSettings>;
        scheduledAt?: number;
      }[];
    }) => youtubeApi.bulkDrafts(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.youtube.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.posts.all });
    },
  });
}

export function useYouTubeBulkSchedule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: youtubeApi.bulkSchedule,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.youtube.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.posts.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.schedule.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.distributions.all });
    },
  });
}

/** Uploads this page is sending straight to YouTube, live. */
export function useDirectUploads(): DirectUploadEntry[] {
  return useSyncExternalStore(
    subscribeDirectUploads,
    directUploadsSnapshot,
    directUploadsSnapshot
  );
}
